import { useOneShotAction } from '@/components/features/auth/status';
import { accountRecoveryApi } from '@/lib/api-client';
import { resolveRequestRestoreError } from '@/lib/account/restore-error';
import { DeactivatedAccountView } from './DeactivatedAccountView';

/**
 * The soft-deleted-account branch of sign-in.
 *
 * Its own screen, not a block inside the form. The old version wedged an amber
 * panel between the password field and the submit button, which reads as a
 * validation message - but the credentials were correct, and the form is no
 * longer the thing to interact with. Promoting it also retires the last
 * raw-palette block in auth (`border-amber-200 bg-amber-50 text-amber-900`
 * against `dark:border-warning/30`, a palette in light and tokens in dark on
 * the same element).
 *
 * The request goes through `useOneShotAction`, the same hook `AuthSuccess`'s
 * resend uses. This screen used to keep its own copy of the pending/done/error
 * state and, having a copy, drifted: it UNMOUNTED its button when done while
 * `AuthSuccess` kept the button and swapped the label. It now keeps the button.
 */
export function DeactivatedAccountCard({
  email,
  onUseDifferentAccount,
}: {
  email: string;
  onUseDifferentAccount: () => void;
}) {
  // Our backend, not Supabase — so it resolves failures through the restore
  // table rather than the auth classifier. That table already has the copy for
  // the two statuses that matter here: the `5/minute` rate limit and a dead
  // connection.
  const request = useOneShotAction(
    () => accountRecoveryApi.requestRestore(email),
    resolveRequestRestoreError
  );

  return (
    <DeactivatedAccountView
      email={email}
      isPending={request.isPending}
      isDone={request.isDone}
      error={request.error}
      onRequest={request.run}
      onUseDifferentAccount={onUseDifferentAccount}
    />
  );
}
